import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { Reveal } from "./Reveal";

const PLATFORM_LABEL = { tiktok: "TikTok", instagram: "Instagram", youtube_short: "YouTube Shorts", facebook: "Facebook" };

// "Inside the Código": lightweight thumbnails linking out to social posts (no third-party embeds).
export function SocialGrid() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/social-posts").then((r) => setPosts(r.data || [])).catch(() => {}); }, []);
  if (posts.length === 0) return null;
  return (
    <section data-testid="social-grid" className="px-6 py-16 sm:px-10 lg:px-16">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-gold">Inside the Código</p>
          <h2 className="mb-8 font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-5xl">Latest from DJ Código</h2>
        </Reveal>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {posts.slice(0, 6).map((p, i) => (
            <Reveal key={p.internalId} delay={i * 0.04}>
              <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`social-post-${i}`} onClick={() => track(`${p.platform.replace("youtube_short", "youtube")}_click`, { metadata: { placement: "social_grid", url: p.url } })} className="group relative block aspect-[9/16] overflow-hidden rounded-2xl border border-white/10 bg-surface/60">
                {p.thumbnail && <img src={p.thumbnail} alt={p.caption || `DJ Código on ${PLATFORM_LABEL[p.platform] || p.platform}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                <span className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
                <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-gold">{PLATFORM_LABEL[p.platform] || p.platform}</span>
                <span className="absolute bottom-2 left-2 right-2 line-clamp-2 text-[11px] font-medium text-white/90">{p.caption}</span>
                <ExternalLink size={12} className="absolute bottom-2 right-2 text-white/60" />
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// "Behind the Código": latest news / stories teaser.
export function NewsTeaser({ limit = 3 }) {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get(`/posts?limit=${limit}`).then((r) => setPosts(r.data || [])).catch(() => {}); }, [limit]);
  if (posts.length === 0) return null;
  return (
    <section data-testid="news-teaser" className="px-6 py-16 sm:px-10 lg:px-16">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-gold">Behind the Código</p>
              <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-5xl">News & Stories</h2>
            </div>
            <Link to="/news" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-white/60 hover:text-gold">All stories <ArrowRight size={14} /></Link>
          </div>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {posts.map((p, i) => <PostCard key={p.internalId} post={p} index={i} />)}
        </div>
      </div>
    </section>
  );
}

export function PostCard({ post, index = 0 }) {
  return (
    <Reveal delay={index * 0.05}>
      <Link to={`/news/${post.slug}`} data-testid={`post-${post.slug}`} className="group block overflow-hidden rounded-2xl border border-white/10 bg-surface/60 transition-colors hover:border-gold/30">
        {post.coverImage && <div className="aspect-[16/10] overflow-hidden"><img src={post.coverImage} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>}
        <div className="p-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-gold">{post.category} • {post.publishedAt?.slice(0, 10)}</p>
          <h3 className="mt-2 font-display text-xl font-semibold leading-tight text-white">{post.title}</h3>
          {post.excerpt && <p className="mt-2 line-clamp-3 text-sm text-white/60">{post.excerpt}</p>}
        </div>
      </Link>
    </Reveal>
  );
}
